import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  faBuilding,
  faCalendar,
  faFileInvoiceDollar,
  faGrid2,
  faLayerGroup,
  faMessages,
  faUserPlus,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faBuilding as faBuildingSolid,
  faCalendar as faCalendarSolid,
  faFileInvoiceDollar as faFileInvoiceDollarSolid,
  faGrid2 as faGrid2Solid,
  faLayerGroup as faLayerGroupSolid,
  faMessages as faMessagesSolid,
  faUserPlus as faUserPlusSolid,
  faUsers as faUsersSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useAppConfigContext } from "contexts/AppConfigContext/useAppConfigContext";
import { useProfileContext } from "contexts/ProfileContext";
import { MarketTypes } from "types";
import { useUnreadMessagesCount } from "utils/contexts/queries/useUnreadMessagesCount";
import {
  isRouteAllowed,
  isUserMarket,
  userIsAdmin,
  userIsBillingAdmin,
  userIsPartner,
  userIsPhysician,
} from "utils/profile/profileHelper";

import { ListItem } from "./ListItem";

export const Menu: React.FC<Record<string, never>> = () => {
  const { navBarCollapsed } = useAppConfigContext();
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const location = useLocation();
  const listRef = useRef<HTMLUListElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{ top: number; height: number } | null>(null);

  const { unreadMessagesCount } = useUnreadMessagesCount();
  const ifAllowed = (route: string) => !!(profile && isRouteAllowed(profile, route));

  invariant(profile, "Profile should be defined");
  const isUS = isUserMarket(MarketTypes.US, profile);
  const isGB = isUserMarket(MarketTypes.GB, profile);
  const isPartner = userIsPartner(profile);
  const isPhysician = userIsPhysician(profile);

  const updateIndicator = useCallback(() => {
    if (!listRef.current) return;

    const activeItem = listRef.current.querySelector("[data-active='true']") as HTMLElement;
    if (activeItem) {
      const listRect = listRef.current.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      setIndicatorStyle({
        top: itemRect.top - listRect.top,
        height: itemRect.height,
      });
    } else {
      setIndicatorStyle(null);
    }
  }, []);

  useEffect(() => {
    updateIndicator();
  }, [location.pathname, updateIndicator]);

  useEffect(() => {
    // Update on collapse change after transition
    const timer = setTimeout(updateIndicator, 200);
    return () => clearTimeout(timer);
  }, [navBarCollapsed, updateIndicator]);

  return (
    <ListWrapper>
      {indicatorStyle && (
        <SlidingIndicator
          $collapsed={navBarCollapsed}
          style={{
            transform: `translateY(${indicatorStyle.top}px)`,
            height: indicatorStyle.height,
          }}
        />
      )}
      <List ref={listRef}>
        {ifAllowed("/dashboard") && (
          <ListItem
            to="/dashboard"
            label={t("views.dashboard")}
            icon={<FontAwesomeIcon icon={faGrid2} />}
            iconActive={<FontAwesomeIcon icon={faGrid2Solid} />}
          />
        )}

        {ifAllowed("/messages") && (
          <ListItem
            to="/messages"
            label={t("views.messages")}
            icon={<FontAwesomeIcon icon={faMessages} />}
            iconActive={<FontAwesomeIcon icon={faMessagesSolid} />}
            notificationCount={unreadMessagesCount}
          />
        )}

        {profile?.therapist_profile?.feature_flags?.includes("BOOKING_V2") && ifAllowed("/calendar") && (
          <ListItem
            to="/calendar"
            label={t("views.calendar")}
            icon={<FontAwesomeIcon icon={faCalendar} />}
            iconActive={<FontAwesomeIcon icon={faCalendarSolid} />}
          />
        )}

        {ifAllowed("/patients") && !isPartner && !isPhysician && (
          <ListItem
            to="/patients"
            label={t("views.patients")}
            icon={<FontAwesomeIcon icon={faUsers} />}
            iconActive={<FontAwesomeIcon icon={faUsersSolid} />}
          />
        )}

        {ifAllowed("/library") && (
          <ListItem
            to="/library/your-library/exercises"
            label={t("views.library")}
            icon={<FontAwesomeIcon icon={faLayerGroup} />}
            iconActive={<FontAwesomeIcon icon={faLayerGroupSolid} />}
          />
        )}

        {ifAllowed("/invitations") && !isGB && (
          <ListItem
            to="/invitations"
            label={t("views.invitations")}
            icon={<FontAwesomeIcon icon={faUserPlus} />}
            iconActive={<FontAwesomeIcon icon={faUserPlusSolid} />}
          />
        )}

        {ifAllowed("/admin") &&
          (profile?.health_care_organization ||
            (isPartner && !userIsAdmin(profile) && !userIsBillingAdmin(profile))) && (
            <ListItem
              to="/admin"
              label={t("views.admin")}
              icon={<FontAwesomeIcon icon={faBuilding} />}
              iconActive={<FontAwesomeIcon icon={faBuildingSolid} />}
            />
          )}

        {isUS && ifAllowed("/admin-billing") && (
          <ListItem
            to="/admin-billing"
            label={t("views.billing")}
            icon={<FontAwesomeIcon icon={faFileInvoiceDollar} />}
            iconActive={<FontAwesomeIcon icon={faFileInvoiceDollarSolid} />}
          />
        )}
      </List>
    </ListWrapper>
  );
};

const List = styled.ul`
  padding: 0;
  height: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  list-style: none;
  justify-content: flex-start;
  gap: 8px;
  margin: 0;
`;

const ListWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SlidingIndicator = styled.div<{ $collapsed: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  background-color: ${props => props.theme.colors.redesign.b10};
  border-radius: 8px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), height 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 0;
`;
